import z from "zod";
import {
  CashRegisterIdScheme,
  LayoutIdScheme,
  ChainIdScheme,
  PaymentMethodIdScheme,
  StoreIdScheme,
  ResellerIdScheme,
  StockLocationIdScheme,
} from "./ids";
import { BookkeepingIntegrationSettingScheme } from "./bookkeepingIntegration";
import { Country } from "./enums/Country";
import { Currency } from "./enums/currency";
import { Language } from "./enums/Language";
import { ProductStyle } from "./enums/ProductStyle";
import { BookkeepingDefaultAccountsScheme } from "./BookkeepingDefaultAccounts";
import { ExtraProductFieldScheme } from "./extraProductField";
import { InvoiceMode } from "./enums/InvoiceMode";
import { LabelDrawableBarcodeSymbology } from "./enums/LabelDrawable";
import { CartMergeMode } from "./enums/CartMergeMode";
import { EndOfDayPaymentMethodMergeMode } from "./enums/EndOfDayPaymentMethodMergeMode";
import { PaymentReportGrouping } from "./enums/PaymentReportGrouping";
import { CartOrderMode } from "./enums/CartOrderMode";
import { TipPromptBehavior } from "./enums/TipPromptBehavior";
import { PrepTicketPromptMode } from "./enums/PrepTicketPromptMode";
import { ServiceOrderDepositVatMode } from "./enums/ServiceOrderDepositVatMode";
import { ServiceOrderWorkflowStepScheme } from "./serviceOrderWorkflow";
import { TipEntryMode } from "./enums/TipEntryMode";
import { TipGridScheme } from "./tipGrid";
import { VariantInputMode } from "./enums/VariantInputMode";
import { OutOfStockBehavior } from "./enums/OutOfStockBehavior";
import { StaffSelectionMode } from "./enums/StaffSelectionMode";
import { PaymentMethodButtonLayoutScheme } from "./paymentMethodButtonLayout";
import { KeypadDecimalEntryMode } from "./enums/KeypadDecimalEntryMode";
import { ProductSyncSettingsScheme } from "./productSyncSettings";
import { CustomerDisplaySettingsScheme } from "./customerDisplaySettings";
import { CatalogPolicyOverridesScheme } from "./catalogPolicy";
import { InventoryCostingSettingsScheme } from "./inventoryCostingSettings";

export const PAYMENT_COMPLETE_AUTO_CLOSE_MIN_SECONDS = 3;
export const PAYMENT_COMPLETE_AUTO_CLOSE_MAX_SECONDS = 120;

export const PaymentCompleteAutoCloseSecondsScheme = z
  .number()
  .int()
  .refine(
    (seconds) =>
      seconds === 0 ||
      (seconds >= PAYMENT_COMPLETE_AUTO_CLOSE_MIN_SECONDS &&
        seconds <= PAYMENT_COMPLETE_AUTO_CLOSE_MAX_SECONDS),
    {
      message: `Must be 0 (off) or between ${PAYMENT_COMPLETE_AUTO_CLOSE_MIN_SECONDS} and ${PAYMENT_COMPLETE_AUTO_CLOSE_MAX_SECONDS} seconds`,
    },
  );

export const StoreDBScheme = z.object({
  id: StoreIdScheme,
  chainId: ChainIdScheme,
  name: z.string(),
  displayName: z.string().optional(),
  city: z.string(),
  street: z.string(),
  postalcode: z.string(),
  orgNumber: z.string(),
  contactPerson: z.string().optional(),
  contactPhone: z.string().optional(),
  contactEmail: z.string().optional(),
  country: z.enum(Country),
  timeZone: z.string(),
  currency: z.enum(Currency),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  active: z.boolean(),
  activeCashRegister: CashRegisterIdScheme.nullable(),
  language: z.enum(Language),
  resellerId: ResellerIdScheme.nullable().optional(),
  invoiceMode: z.enum(InvoiceMode).optional(),

  layoutId: LayoutIdScheme.nullable(),

  // settings
  bookkeepingSeries: z.string().nullable(),
  bookkeepingTextOnVoucher: z.string().nullable(),
  bookkeepingCostcenter: z.string().optional().nullable(),
  bookkeepingIntegration:
    BookkeepingIntegrationSettingScheme.optional().nullable(),
  bookkeepingDefaultAccounts: BookkeepingDefaultAccountsScheme.partial()
    .nullable()
    .optional(),
  automaticEndOfDayTime: z.number().nullable(), // the hour of the day when the end of day is automatically triggered
  businessDayStartHour: z.number().int().min(0).max(12).optional(),
  reportEmail: z.string().nullable(),
  emailReport: z.boolean(),
  emailSalesReportOnEndOfDay: z.boolean().optional(),
  // Deprecated: only older app versions write it; read through resolvePaymentReportGrouping
  endOfDayPaymentMethodMergeMode: z
    .enum(EndOfDayPaymentMethodMergeMode)
    .nullable()
    .optional(),
  paymentReportGrouping: z.enum(PaymentReportGrouping).optional(),
  receiptText: z.string().nullable(),
  showReferenceOnReceipt: z.boolean().optional(),
  defaultReceiptPrinter: z.string().nullable(),
  printRefundSignatureReceipt: z.boolean().optional(),
  printPayoutSignatureReceipt: z.boolean().optional(),
  printReceiptAutomatically: z.boolean().optional(),
  emailReceiptEnabled: z.boolean().optional(),
  printNewGiftcardAfterPurchase: z.boolean().optional(),
  printNewCreditNoteAfterPurchase: z.boolean().optional(),
  showArticleNumberInReceipt: z.boolean().optional(),
  showDisplayNameOnReceipt: z.boolean().optional(),
  showBarcodeOnReceipt: z.boolean().optional(),
  showReceiptQr: z.boolean().optional(),
  paymentCompleteAutoCloseSeconds:
    PaymentCompleteAutoCloseSecondsScheme.nullable().optional(),

  enablePriceInDecimals: z.boolean().nullable().optional(),
  enableOpenPriceInDecimals: z.boolean().nullable().optional(),
  keypadInverted: z.boolean().nullable().optional(),
  keypadDecimalEntryMode: z.enum(KeypadDecimalEntryMode).nullable().optional(),

  restoreSaleAfterRestart: z.boolean().nullable().optional(),

  enableBarcodeScanner: z.boolean().nullable(),
  enableCameraScanning: z.boolean().nullable(),
  logo: z.string().nullable(),
  receiptLogo: z
    .object({
      base64Png: z.string(),
      base64Bytes: z.string(),
      width: z.number(),
      height: z.number(),
    })
    .nullable(),
  receiptLogoBinarizationThreshold: z.number(),
  numberOfColumnsInStore: z.number().nullable(),
  productStyle: z.enum(ProductStyle).nullable(),
  paymentMethodsOrder: z.array(PaymentMethodIdScheme).nullable(),
  paymentMethodButtonLayout:
    PaymentMethodButtonLayoutScheme.nullable().optional(),
  customerDisplayActive: z.boolean().nullable(),
  customerDisplayImage: z.string().nullable(),
  customerDisplayScaling: z.number().nullable(),
  customerDisplaySettings: CustomerDisplaySettingsScheme.nullable().optional(),
  giftCardValidityDays: z.number().optional(),
  creditNoteValidityDays: z.number().optional(),
  extraProductFields: z.array(ExtraProductFieldScheme).nullable().optional(),
  productSyncIntervalMinutes: z.number().optional(),
  productFullSyncIntervalMinutes: z.number().optional(),
  fullProductSyncOnStart: z.boolean().optional(),
  recoverSale: z.boolean().optional(),

  barcodeGenerationType: z
    .enum([
      LabelDrawableBarcodeSymbology.EAN8,
      LabelDrawableBarcodeSymbology.EAN13,
    ])
    .nullable()
    .optional(),
  barcodeGenerationPrefix: z.number().nullable().optional(),
  cartMergeMode: z.enum(CartMergeMode).nullable().optional(),
  cartOrderMode: z.enum(CartOrderMode).nullable().optional(),
  fullscreenFloorPlan: z.boolean().nullable().optional(),

  tipPromptBehavior: z.enum(TipPromptBehavior).nullable().optional(),
  prepTicketPromptMode: z.enum(PrepTicketPromptMode).nullable().optional(),
  referenceDigitCount: z
    .union([z.literal(2), z.literal(3), z.literal(4)])
    .optional(),
  serviceOrderDepositVatMode: z
    .enum(ServiceOrderDepositVatMode)
    .nullable()
    .optional(),
  serviceOrderPrinterId: z.string().nullable().optional(),
  serviceOrderText: z.string().nullable().optional(),
  serviceOrderWorkflow: z
    .array(ServiceOrderWorkflowStepScheme)
    .nullable()
    .optional(),
  tipEntryMode: z.enum(TipEntryMode).nullable().optional(),
  tipGrid: TipGridScheme.nullable().optional(),
  tipShowTipAmountOnPresetButtons: z.boolean().nullable().optional(),
  tipShowSkipTipButton: z.boolean().nullable().optional(),

  showStockInStore: z.boolean().nullable().optional(),
  stockLocationId: StockLocationIdScheme.nullable().optional(),
  outOfStockBehavior: z.enum(OutOfStockBehavior).nullable().optional(),
  // FIFO costing and serial numbers; missing means plain purchasePrice.
  // Set only through activation, which writes the opening cost layers.
  inventoryCosting: InventoryCostingSettingsScheme.nullable().optional(),
  variantInputMode: z.enum(VariantInputMode).nullable().optional(),

  // staff
  staffSelectionMode: z.enum(StaffSelectionMode).nullable().optional(),
  autoSignOutAfterMinutes: z.number().nullable().optional(),
  signOutAfterSale: z.boolean().optional(),
  hideChangeUserButton: z.boolean().nullable().optional(),
  showStaffOnReceipt: z.boolean().optional(),

  // reseller settings
  showResellerLogoInCustomerDisplay: z.boolean().optional(),
  resellerLogoScaling: z.number().optional(),
  resellerLogoInCustomerDisplay: z.string().nullable().optional(),
  resellerLogoBackgroundColor: z.string().nullable().optional(),

  alwaysApplyRounding: z.boolean().optional(),
  defaultVAT: z.number().optional(),

  productSyncSettings: ProductSyncSettingsScheme.optional().nullable(),
  catalogPolicyOverrides: CatalogPolicyOverridesScheme.optional(),
});

export const RestGetStore = StoreDBScheme;
export const RestCreateStore = StoreDBScheme.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  inventoryCosting: true,
});
export const RestUpdateStore = StoreDBScheme.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  inventoryCosting: true,
}).partial();

export type DBStore = z.infer<typeof StoreDBScheme>;
export type RestStore = z.infer<typeof RestGetStore>;
export type RestCreateStore = z.infer<typeof RestCreateStore>;
export type RestUpdateStore = z.infer<typeof RestUpdateStore>;
